import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import { categories, mainSiteUrl } from "@/mocks/categories";
import { articles } from "@/mocks/articles";
import { useSeo } from "@/hooks/useSeo";
import { SITE_URL, SITE_NAME, buildWebsiteJsonLd, languageCode } from "@/lib/seo";

export default function Home() {
  const { t } = useTranslation();
  const { lang } = useParams<{ lang: string }>();
  const navigate = useNavigate();
  const currentLang = lang || "pt";
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const getCategoryName = (cat: typeof categories[0]) => {
    if (currentLang === "pt") return cat.namePt;
    if (currentLang === "de") return cat.nameDe;
    return cat.name;
  };

  const getArticleContent = (a: typeof articles[0]) => {
    if (currentLang === "pt") return a.pt;
    if (currentLang === "de") return a.de;
    return a.en;
  };

  const goToArticle = (slug: string) => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    navigate(`/${currentLang}/article/${slug}/`);
  };

  const resourceCards = [
    { key: "resources.products", icon: "ri-shopping-bag-3-line", color: "bg-primary-100 text-primary-700" },
    { key: "resources.sellers", icon: "ri-user-star-line", color: "bg-accent-100 text-accent-700" },
    { key: "resources.qc", icon: "ri-camera-line", color: "bg-secondary-100 text-secondary-700" },
    { key: "resources.shipping", icon: "ri-truck-line", color: "bg-primary-100 text-primary-700" },
    { key: "resources.warehouse", icon: "ri-building-2-line", color: "bg-accent-100 text-accent-700" },
    { key: "resources.safety", icon: "ri-shield-check-line", color: "bg-secondary-100 text-secondary-700" },
  ];

  const comparisonData = [
    { factor: "compare.product", check: "compare.product.check", why: "compare.product.why" },
    { factor: "compare.seller", check: "compare.seller.check", why: "compare.seller.why" },
    { factor: "compare.qc", check: "compare.qc.check", why: "compare.qc.why" },
    { factor: "compare.price", check: "compare.price.check", why: "compare.price.why" },
    { factor: "compare.shipping", check: "compare.shipping.check", why: "compare.shipping.why" },
  ];

  const faqData = [
    {
      q: currentLang === "pt" ? "O que é CSSBuy Spreadsheet?" : currentLang === "de" ? "Was ist CSSBuy Spreadsheet?" : "What is CSSBuy Spreadsheet?",
      a: currentLang === "pt" ? "CSSBuy Spreadsheet é uma ferramenta prática para organizar informações sobre produtos, vendedores, preços e categorias antes de comprar." : currentLang === "de" ? "CSSBuy Spreadsheet ist ein praktisches Tool zur Organisation von Produkt-, Verkäufer-, Preis- und Kategorieinformationen vor dem Kauf." : "CSSBuy Spreadsheet is a practical tool for organizing product, seller, price and category information before purchasing."
    },
    {
      q: currentLang === "pt" ? "Como usar CSSBuy Spreadsheet?" : currentLang === "de" ? "Wie verwende ich CSSBuy Spreadsheet?" : "How to use CSSBuy Spreadsheet?",
      a: currentLang === "pt" ? "Navegue pelas categorias, compare vendedores, verifique fotos de QC e avalie custos de envio antes de tomar decisões." : currentLang === "de" ? "Durchsuchen Sie Kategorien, vergleichen Sie Verkäufer, überprüfen Sie QC-Fotos und bewerten Sie Versandkosten vor Entscheidungen." : "Browse categories, compare sellers, check QC photos and evaluate shipping costs before making decisions."
    },
    {
      q: currentLang === "pt" ? "CSSBuy é seguro?" : currentLang === "de" ? "Ist CSSBuy sicher?" : "Is CSSBuy safe?",
      a: currentLang === "pt" ? "A segurança depende de verificar vendedores, usar métodos de pagamento protegidos e analisar fotos de QC antes de confirmar compras." : currentLang === "de" ? "Die Sicherheit hängt davon ab, Verkäufer zu überprüfen, geschützte Zahlungsmethoden zu verwenden und QC-Fotos vor Bestätigung zu analysieren." : "Safety depends on verifying sellers, using protected payment methods, and analyzing QC photos before confirming purchases."
    },
    {
      q: currentLang === "pt" ? "Quanto custa o envio CSSBuy?" : currentLang === "de" ? "Wie viel kostet CSSBuy-Versand?" : "How much is CSSBuy shipping?",
      a: currentLang === "pt" ? "O custo de envio varia conforme destino, peso, volume, linha de envio e dimensões da parcela. Verifique sempre as taxas atuais." : currentLang === "de" ? "Versandkosten variieren je nach Ziel, Gewicht, Volumen, Versandlinie und Paketabmessungen. Überprüfen Sie immer aktuelle Tarife." : "Shipping costs vary by destination, weight, volume, shipping line and parcel dimensions. Always verify current rates."
    },
  ];

  const heroImage =
    "https://readdy.ai/api/search-image?query=Curated%20streetwear%20fashion%20collection%20with%20sneakers%20hoodie%20caps%20and%20accessories%20arranged%20on%20warm%20cream%20background%20with%20soft%20editorial%20lighting%2C%20clean%20minimal%20aesthetic%20luxury%20product%20flat%20lay&width=1600&height=900&seq=hero-main-v2-01&orientation=landscape";

  const seoTitle =
    currentLang === "pt"
      ? "CSSBuy Spreadsheet: Encontre Produtos e Vendedores com Mais Facilidade | CSSBuyBR"
      : currentLang === "de"
        ? "CSSBuy Spreadsheet: Produkte und Verkäufer leichter finden | CSSBuyBR"
        : "CSSBuy Spreadsheet: Complete Product & Seller Guide 2026 | CSSBuyBR";

  const seoDescription =
    currentLang === "pt"
      ? "Explore um guia independente e atualizado para pesquisar produtos, comparar vendedores, entender QC e avaliar opções de envio com o CSSBuy Spreadsheet."
      : currentLang === "de"
        ? "Entdecken Sie einen unabhängigen, aktuellen Guide, um Produkte zu recherchieren, Verkäufer zu vergleichen, QC zu verstehen und Versandoptionen zu bewerten."
        : "Explore an independent, up-to-date guide to research products, compare sellers, understand QC and evaluate shipping options with CSSBuy Spreadsheet.";

  const seoKeywords =
    currentLang === "pt"
      ? "cssbuy spreadsheet, cssbuy, cssbuy review, cssbuy shipping, cssbuy guia, cssbuy produtos, cssbuy vendedores"
      : currentLang === "de"
        ? "cssbuy spreadsheet, cssbuy, cssbuy review, cssbuy versand, cssbuy guide, cssbuy produkte, cssbuy verkäufer"
        : "cssbuy spreadsheet, cssbuy, cssbuy review, cssbuy shipping, cssbuy guide, cssbuy products, cssbuy sellers";

  const homeCanonical = `${SITE_URL}/${currentLang}/`;

  useSeo(
    {
      title: seoTitle,
      description: seoDescription,
      keywords: seoKeywords,
      canonical: homeCanonical,
      ogTitle: seoTitle,
      ogDescription: seoDescription,
      ogType: "website",
      ogUrl: homeCanonical,
      ogImage: heroImage,
      ogSiteName: SITE_NAME,
      twitterCard: "summary_large_image",
      twitterTitle: seoTitle,
      twitterDescription: seoDescription,
      twitterImage: heroImage,
    },
    buildWebsiteJsonLd({
      description: seoDescription,
      canonical: homeCanonical,
      inLanguage: languageCode(currentLang),
    })
  );

  return (
    <div className="min-h-screen bg-background-50">
      <Header transparent />

      <section className="relative min-h-[600px] md:min-h-[700px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Curated%20streetwear%20fashion%20collection%20with%20sneakers%20hoodie%20caps%20and%20accessories%20arranged%20on%20warm%20cream%20background%20with%20soft%20editorial%20lighting%2C%20clean%20minimal%20aesthetic%20luxury%20product%20flat%20lay&width=1600&height=900&seq=hero-main-v2-01&orientation=landscape"
            alt="CSSBuy Spreadsheet Products"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/60"></div>
        </div>
        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-6">
            <span className="w-2 h-2 rounded-full bg-accent-400"></span>
            <span className="text-sm text-white/90 font-medium">{t("hero.trust")}</span>
          </div>
          <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl text-white mb-6 leading-tight drop-shadow-md">
            {t("hero.title")}
          </h1>
          <p className="text-base md:text-lg text-white/90 max-w-2xl mx-auto mb-8 leading-relaxed">
            {t("hero.subtitle")}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="px-8 py-3.5 bg-primary-500 text-white rounded-lg font-medium hover:bg-primary-600 transition-all whitespace-nowrap shadow-lg shadow-primary-500/20"
            >
              {t("cta.mainSite")}
            </a>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="px-8 py-3.5 bg-white/10 backdrop-blur-sm text-white border border-white/30 rounded-lg font-medium hover:bg-white/20 transition-all whitespace-nowrap"
            >
              {t("cta.buyNow")}
            </a>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 px-4 md:px-6 lg:px-8 bg-background-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-3">
              {t("categories.title")}
            </h2>
            <p className="text-foreground-600 max-w-xl mx-auto">
              {t("categories.subtitle")}
            </p>
          </div>

          <div className="relative max-w-md mx-auto mb-10" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="w-full flex items-center justify-between px-5 py-3.5 bg-background-50 border-2 border-background-300 rounded-xl text-left hover:border-primary-400 transition-colors"
            >
              <span className={selectedCategory ? "text-foreground-900 font-medium" : "text-foreground-400"}>
                {selectedCategory || t("categories.dropdown.placeholder")}
              </span>
              <i className={`ri-arrow-down-s-line text-lg text-foreground-400 transition-transform ${dropdownOpen ? "rotate-180" : ""}`}></i>
            </button>
            {dropdownOpen && (
              <div className="absolute z-20 w-full mt-2 bg-background-50 rounded-xl border border-background-300 shadow-xl max-h-80 overflow-auto">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => {
                      setSelectedCategory(getCategoryName(cat));
                      setDropdownOpen(false);
                    }}
                    className="flex items-center gap-3 px-5 py-3 hover:bg-background-100 transition-colors border-b border-background-200/50 last:border-0"
                  >
                    <img
                      src={cat.image}
                      alt={getCategoryName(cat)}
                      className="w-8 h-8 rounded-md object-cover flex-shrink-0"
                    />
                    <span className="text-sm font-medium text-foreground-700">{getCategoryName(cat)}</span>
                    <i className="ri-external-link-line ml-auto text-xs text-foreground-400"></i>
                  </a>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3">
            {categories.map((cat) => (
              <a
                key={cat.name}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="group flex flex-col overflow-hidden bg-background-50 border border-background-200/70 rounded-xl hover:border-accent-300 hover:shadow-sm transition-all"
              >
                <div className="w-full h-20 md:h-24 overflow-hidden">
                  <img
                    src={cat.image}
                    alt={getCategoryName(cat)}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-2.5 flex items-center justify-center min-h-[2.5rem]">
                  <span className="text-xs font-medium text-foreground-700 text-center leading-tight">{getCategoryName(cat)}</span>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      <section id="about" className="py-16 md:py-20 px-4 md:px-6 lg:px-8 bg-background-100">
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-block px-3 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full mb-4">
            {currentLang === "pt" ? "Introdução" : currentLang === "de" ? "Einführung" : "Introduction"}
          </span>
          <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-6">
            {t("intro.title")}
          </h2>
          <p className="text-foreground-700 leading-relaxed text-base md:text-lg max-w-3xl mx-auto">
            {t("intro.text")}
          </p>
        </div>
      </section>

      <section id="resources" className="py-16 md:py-20 px-4 md:px-6 lg:px-8 bg-background-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-3">
              {t("resources.title")}
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {resourceCards.map((card) => (
              <div key={card.key} className="p-6 bg-background-50 border border-background-200/70 rounded-xl hover:shadow-sm transition-all">
                <div className={`w-11 h-11 flex items-center justify-center rounded-lg ${card.color} mb-4`}>
                  <i className={`${card.icon} text-lg`}></i>
                </div>
                <h3 className="font-heading text-lg text-foreground-900 mb-2">{t(card.key)}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{t(`${card.key}.desc`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 px-4 md:px-6 lg:px-8 bg-background-100">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-3">
              {t("compare.title")}
            </h2>
          </div>
          <div className="overflow-x-auto rounded-xl border border-background-200/70">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-background-200/50">
                  <th className="px-5 py-3 text-left font-semibold text-foreground-900">{t("compare.factor")}</th>
                  <th className="px-5 py-3 text-left font-semibold text-foreground-900">{t("compare.check")}</th>
                  <th className="px-5 py-3 text-left font-semibold text-foreground-900">{t("compare.why")}</th>
                </tr>
              </thead>
              <tbody>
                {comparisonData.map((row) => (
                  <tr key={row.factor} className="border-t border-background-200/50 hover:bg-background-50/50 transition-colors">
                    <td className="px-5 py-3.5 font-medium text-foreground-900">{t(row.factor)}</td>
                    <td className="px-5 py-3.5 text-foreground-700">{t(row.check)}</td>
                    <td className="px-5 py-3.5 text-foreground-700">{t(row.why)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section id="review" className="py-16 md:py-20 px-4 md:px-6 lg:px-8 bg-background-50">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full mb-4">
                Review
              </span>
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-4">
                {currentLang === "pt" ? "CSSBuy Review: o que você deve verificar antes de usar" : currentLang === "de" ? "CSSBuy Review: Was Sie vor der Nutzung prüfen sollten" : "CSSBuy Review: What You Should Check Before Using"}
              </h2>
              <p className="text-foreground-700 leading-relaxed mb-6">
                {currentLang === "pt"
                  ? "Antes de usar CSSBuy, analise como funciona a plataforma, pontos positivos, limitações, opções de envio, warehouse, QC, pagamento e suporte ao cliente."
                  : currentLang === "de"
                    ? "Bevor Sie CSSBuy nutzen, analysieren Sie, wie die Plattform funktioniert, Stärken, Schwächen, Versandoptionen, Warehouse, QC, Zahlung und Kundensupport."
                    : "Before using CSSBuy, analyze how the platform works, strengths, limitations, shipping options, warehouse, QC, payment and customer support."}
              </p>
              <button
                onClick={() => goToArticle("cssbuy-review")}
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white rounded-lg font-medium hover:bg-primary-600 transition-all whitespace-nowrap"
              >
                {currentLang === "pt" ? "Ler CSSBuy Review completo" : currentLang === "de" ? "Vollständiges CSSBuy Review lesen" : "Read Full CSSBuy Review"}
                <i className="ri-arrow-right-line"></i>
              </button>
            </div>
            <div className="rounded-2xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20warehouse%20logistics%20interior%20with%20organized%20shelves%20and%20cardboard%20boxes%20in%20warm%20ambient%20lighting%20clean%20professional%20environment&width=800&height=600&seq=review-section-v2-01&orientation=landscape"
                alt="CSSBuy Review"
                className="w-full h-64 md:h-80 object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      <section id="shipping" className="py-16 md:py-20 px-4 md:px-6 lg:px-8 bg-background-100">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div className="order-2 md:order-1 rounded-2xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=International%20shipping%20logistics%20with%20stacked%20parcels%20and%20delivery%20boxes%20on%20clean%20neutral%20background%20with%20soft%20world%20map%20illustration%2C%20minimal%20style&width=800&height=600&seq=shipping-section-v2-01&orientation=landscape"
                alt="CSSBuy Shipping"
                className="w-full h-64 md:h-80 object-cover"
              />
            </div>
            <div className="order-1 md:order-2">
              <span className="inline-block px-3 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full mb-4">
                {currentLang === "pt" ? "Envio" : currentLang === "de" ? "Versand" : "Shipping"}
              </span>
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-4">
                {currentLang === "pt" ? "CSSBuy Shipping Cost: compare antes de enviar" : currentLang === "de" ? "CSSBuy Shipping Cost: Vor dem Versand vergleichen" : "CSSBuy Shipping Cost: Compare Before Sending"}
              </h2>
              <p className="text-foreground-700 leading-relaxed mb-6">
                {currentLang === "pt"
                  ? "O custo de envio depende de destino, peso, volume, linha de envio, dimensões da parcela e taxas atuais. Sempre verifique as informações mais recentes antes de enviar."
                  : currentLang === "de"
                    ? "Versandkosten hängen ab von Ziel, Gewicht, Volumen, Versandlinie, Paketabmessungen und aktuellen Tarifen. Überprüfen Sie immer aktuelle Informationen vor dem Versand."
                    : "Shipping costs depend on destination, weight, volume, shipping line, parcel dimensions and current rates. Always verify latest information before shipping."}
              </p>
              <button
                onClick={() => goToArticle("cssbuy-shipping-cost")}
                className="inline-flex items-center gap-2 px-6 py-3 bg-accent-500 text-white rounded-lg font-medium hover:bg-accent-600 transition-all whitespace-nowrap"
              >
                {currentLang === "pt" ? "Ver Guia de Shipping" : currentLang === "de" ? "Versand-Guide ansehen" : "View Shipping Guide"}
                <i className="ri-arrow-right-line"></i>
              </button>
            </div>
          </div>
        </div>
      </section>

      <section id="qc" className="py-16 md:py-20 px-4 md:px-6 lg:px-8 bg-background-50">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <span className="inline-block px-3 py-1 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-full mb-4">
                QC
              </span>
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-4">
                {currentLang === "pt" ? "CSSBuy QC: entenda as fotos de qualidade" : currentLang === "de" ? "CSSBuy QC: Qualitätsfotos verstehen" : "CSSBuy QC: Understand Quality Photos"}
              </h2>
              <p className="text-foreground-700 leading-relaxed mb-6">
                {currentLang === "pt"
                  ? "QC (Quality Check) permite verificar detalhes do produto antes do envio. Analise fotos de múltiplos ângulos, materiais, costuras e embalagem."
                  : currentLang === "de"
                    ? "QC (Quality Check) ermöglicht die Überprüfung von Produktdetails vor dem Versand. Analysieren Sie Fotos aus mehreren Winkeln, Materialien, Nähte und Verpackung."
                    : "QC (Quality Check) allows you to verify product details before shipping. Analyze photos from multiple angles, materials, stitching and packaging."}
              </p>
              <button
                onClick={() => goToArticle("cssbuy-qc-guide")}
                className="inline-flex items-center gap-2 px-6 py-3 bg-secondary-500 text-white rounded-lg font-medium hover:bg-secondary-600 transition-all whitespace-nowrap"
              >
                {currentLang === "pt" ? "Guia de QC" : currentLang === "de" ? "QC-Guide" : "QC Guide"}
                <i className="ri-arrow-right-line"></i>
              </button>
            </div>
            <div className="rounded-2xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Product%20quality%20inspection%20with%20clothing%20detail%20close%20up%20stitching%20and%20fabric%20texture%20on%20clean%20white%20background%20professional%20photography%20soft%20lighting&width=800&height=600&seq=qc-section-v2-01&orientation=landscape"
                alt="CSSBuy QC"
                className="w-full h-64 md:h-80 object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      <section id="sellers" className="py-16 md:py-20 px-4 md:px-6 lg:px-8 bg-background-100">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-3">
              {currentLang === "pt" ? "Vendedores CSSBuy" : currentLang === "de" ? "CSSBuy Verkäufer" : "CSSBuy Sellers"}
            </h2>
            <p className="text-foreground-600 max-w-2xl mx-auto">
              {currentLang === "pt"
                ? "Compare vendedores, verifique histórico e avaliações antes de comprar."
                : currentLang === "de"
                  ? "Vergleichen Sie Verkäufer, überprüfen Sie Historie und Bewertungen vor dem Kauf."
                  : "Compare sellers, verify history and reviews before buying."}
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: "ri-user-search-line", title: currentLang === "pt" ? "Pesquisar Vendedores" : currentLang === "de" ? "Verkäufer suchen" : "Find Sellers", desc: currentLang === "pt" ? "Use filtros e categorias para encontrar vendedores relevantes." : currentLang === "de" ? "Verwenden Sie Filter und Kategorien, um relevante Verkäufer zu finden." : "Use filters and categories to find relevant sellers." },
              { icon: "ri-bar-chart-line", title: currentLang === "pt" ? "Comparar" : currentLang === "de" ? "Vergleichen" : "Compare", desc: currentLang === "pt" ? "Analise preços, avaliações e histórico lado a lado." : currentLang === "de" ? "Analysieren Sie Preise, Bewertungen und Historie nebeneinander." : "Analyze prices, reviews and history side by side." },
              { icon: "ri-shield-check-line", title: currentLang === "pt" ? "Verificar Segurança" : currentLang === "de" ? "Sicherheit prüfen" : "Verify Safety", desc: currentLang === "pt" ? "Confirme reputação e métodos de pagamento aceitos." : currentLang === "de" ? "Bestätigen Sie Reputation und akzeptierte Zahlungsmethoden." : "Confirm reputation and accepted payment methods." },
            ].map((item, i) => (
              <div key={i} className="p-6 bg-background-50 border border-background-200/70 rounded-xl">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 text-primary-600 mb-3">
                  <i className={`${item.icon} text-lg`}></i>
                </div>
                <h3 className="font-heading text-base text-foreground-900 mb-1">{item.title}</h3>
                <p className="text-sm text-foreground-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="articles" className="py-16 md:py-20 px-4 md:px-6 lg:px-8 bg-background-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-3">
              {t("articles.title")}
            </h2>
            <p className="text-foreground-600 max-w-xl mx-auto">
              {t("articles.subtitle")}
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {articles.map((article) => {
              const c = getArticleContent(article);
              return (
                <button
                  key={article.slug}
                  onClick={() => goToArticle(article.slug)}
                  className="group text-left bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-accent-300 hover:shadow-sm transition-all cursor-pointer"
                >
                  <div className="w-full h-40 overflow-hidden">
                    <img
                      src={article.image}
                      alt={c.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-5">
                    <span className="inline-block px-2 py-0.5 bg-accent-100 text-accent-700 text-xs font-medium rounded-full mb-2">
                      {article.category}
                    </span>
                    <h3 className="font-heading text-base text-foreground-900 mb-2 leading-snug line-clamp-2">
                      {c.title}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed line-clamp-3">
                      {c.excerpt}
                    </p>
                    <span className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 mt-3 group-hover:text-primary-700 transition-colors">
                      {t("articles.readMore")}
                      <i className="ri-arrow-right-line"></i>
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      <section id="safety" className="py-16 md:py-20 px-4 md:px-6 lg:px-8 bg-background-100">
        <div className="max-w-4xl mx-auto">
          <div className="bg-background-50 border border-secondary-200/60 rounded-xl p-6 md:p-8">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 flex-shrink-0">
                <i className="ri-shield-check-line text-lg"></i>
              </div>
              <div>
                <h2 className="font-heading text-xl text-foreground-950 mb-3">
                  {currentLang === "pt" ? "Centro de Segurança" : currentLang === "de" ? "Sicherheitszentrum" : "Safety Center"}
                </h2>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {t("safety.notice")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 px-4 md:px-6 lg:px-8 bg-background-50">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-3">
              FAQ
            </h2>
          </div>
          <div className="space-y-3">
            {faqData.map((faq, i) => (
              <div key={i} className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-background-100/50 transition-colors"
                >
                  <span className="font-medium text-foreground-900 text-sm pr-4">{faq.q}</span>
                  <i className={`ri-arrow-down-s-line text-lg text-foreground-400 flex-shrink-0 transition-transform ${openFaq === i ? "rotate-180" : ""}`}></i>
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 px-4 md:px-6 lg:px-8 bg-primary-500">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-3xl text-white mb-4">
            {t("cta.explore")}
          </h2>
          <p className="text-white/80 mb-8 max-w-xl mx-auto">
            {currentLang === "pt"
              ? "Acesse o CSSBuy Spreadsheet e encontre produtos, vendedores e recursos de compra."
              : currentLang === "de"
                ? "Greifen Sie auf CSSBuy Spreadsheet zu und finden Sie Produkte, Verkäufer und Kaufressourcen."
                : "Access CSSBuy Spreadsheet and find products, sellers and buying resources."}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="px-8 py-3.5 bg-white text-primary-600 rounded-lg font-medium hover:bg-background-50 transition-all whitespace-nowrap"
            >
              {t("cta.mainSite")}
            </a>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="px-8 py-3.5 bg-white/10 text-white border border-white/30 rounded-lg font-medium hover:bg-white/20 transition-all whitespace-nowrap"
            >
              {t("cta.buyNow")}
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}